<script setup lang="ts">
import { computedAsync } from '@vueuse/core';

import {
  useBrowserStore,
  usePaxStore,
  usePriceCalendarStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { useItineraryStore } from '@apps/main/modules/store/itineraryStore';
import { CoreEventSource } from '@apps/main/new-desktop/core/models';

import { AppPortableText } from '@libs/ui';
import { isExperimentAsync } from '@libs/feature-toggle';

import { PremadeTripType } from '../../models';
import {
  usePremadeTripStore,
  useTripOverviewModalsStore,
  useTripOverviewPageStore,
  useTripOverviewPanelsStore,
} from '../../stores';
import { TabKey } from '../../models/configurationPanel.model';

import AITripDescription from './AITripDescription.vue';
import PremadeTripHeaderDesktop from './PremadeTripHeaderDesktop.vue';
import PremadeTripHeaderMobile from './PremadeTripHeaderMobile.vue';
import TripOverviewHeaderActions from './TripOverviewHeaderActions.vue';

const tripOverviewPageStore = useTripOverviewPageStore();
const tripOverviewModalsStore = useTripOverviewModalsStore();
const tripOverviewPanelsStore = useTripOverviewPanelsStore();

const premadeTripStore = usePremadeTripStore();
const browserStore = useBrowserStore();
const paxStore = usePaxStore();
const statsigEventsStore = useStatsigEventsStore();
const priceCalendarStore = usePriceCalendarStore();
const itineraryStore = useItineraryStore();

interface TripOverviewHeaderProps {
  printMode: boolean;
}

const props = defineProps<TripOverviewHeaderProps>();

const isConfigurationPanelExperiment = computedAsync<boolean>(
  async () => isExperimentAsync('configuration_panel'),
  false
);

function openPaxSelectorModal(): void {
  if (isConfigurationPanelExperiment.value) {
    tripOverviewPanelsStore.openConfigurationPanel(TabKey.Pax);
  } else {
    tripOverviewModalsStore.openPaxSelectorModal(CoreEventSource.TripOverview);
  }
}

function openEditTripModal(): void {
  if (isConfigurationPanelExperiment.value) {
    tripOverviewPanelsStore.openConfigurationPanel(TabKey.Itinerary);
  } else {
    tripOverviewModalsStore.openEditTripModal();
  }
}

function onHomeAirportClick(): void {
  if (isConfigurationPanelExperiment.value) {
    tripOverviewPanelsStore.openConfigurationPanel(TabKey.Airplane);
  } else {
    tripOverviewModalsStore.openHomeAirportSelectorModal();
    statsigEventsStore.logTripOverviewFlyFromButtonClicked();
  }
}

function openMonthsSelectorModal(): void {
  if (isConfigurationPanelExperiment.value) {
    tripOverviewPanelsStore.openConfigurationPanel(TabKey.Calendar);
  } else {
    tripOverviewModalsStore.openMonthsSelectorModal();
  }
}
</script>

<template>
  <template v-if="premadeTripStore.showingPremadeTrip">
    <PremadeTripHeaderMobile
      v-if="browserStore.isSmallDevice && premadeTripStore.premadeTrip"
      :trip-details="premadeTripStore.premadeTrip"
      @edit-trip="openEditTripModal()"
      @show-full-description="
        tripOverviewModalsStore.openPremadeTripDetailsModal
      "
    >
      <template #description>
        <AppPortableText
          v-if="
            premadeTripStore.premadeTrip.type ===
            PremadeTripType.PublishedInSanity
          "
          :portable-text="premadeTripStore.premadeTrip.description"
          type="premade-trip"
        />
        <AITripDescription
          v-if="premadeTripStore.premadeTrip.type === PremadeTripType.AI"
          :description="premadeTripStore.premadeTrip.description"
        />
      </template>
    </PremadeTripHeaderMobile>
    <PremadeTripHeaderDesktop
      v-if="!browserStore.isSmallDevice && premadeTripStore.premadeTrip"
      :trip-details="premadeTripStore.premadeTrip"
      @edit-trip="openEditTripModal()"
      @show-full-description="
        tripOverviewModalsStore.openPremadeTripDetailsModal
      "
    >
      <template #description>
        <AppPortableText
          v-if="
            premadeTripStore.premadeTrip.type ===
            PremadeTripType.PublishedInSanity
          "
          :portable-text="premadeTripStore.premadeTrip.description"
          type="premade-trip"
          class="description"
        />
        <AITripDescription
          v-if="premadeTripStore.premadeTrip.type === PremadeTripType.AI"
          :description="premadeTripStore.premadeTrip.description"
        />
      </template>
    </PremadeTripHeaderDesktop>
  </template>

  <div
    v-if="!browserStore.isSmallDevice && premadeTripStore.showingPremadeTrip"
    class="separator with-side-margin"
  />

  <div v-if="!props.printMode" class="actions">
    <TripOverviewHeaderActions
      :paxes-button-text="paxStore.getPaxesButtonDetailedText"
      :home-flight-data="tripOverviewPageStore.homeFlightData"
      :trip-start-date="itineraryStore.tripStartDate"
      :number-of-destinations="tripOverviewPageStore.numberOfDestinations"
      :number-of-nights="tripOverviewPageStore.numberOfNights"
      :selected-month-option="priceCalendarStore.selectedMonthOption"
      @select-month="priceCalendarStore.selectItineraryMonth"
      @pax-click="openPaxSelectorModal"
      @home-airport-click="onHomeAirportClick"
      @edit-trip="openEditTripModal()"
      @months-selector-click="openMonthsSelectorModal()"
    />
  </div>

  <div
    v-if="browserStore.isSmallDevice && premadeTripStore.showingPremadeTrip"
    class="separator"
  />
</template>

<style scoped lang="scss">
.actions {
  padding: 0 var(--spacing-24) var(--spacing-8) var(--spacing-24);

  @include for-small-devices-max {
    padding: 0 var(--spacing-16) var(--spacing-16) var(--spacing-24);
  }
}

.separator {
  border-bottom: 1px solid var(--color-secondary-90);
  margin-bottom: var(--spacing-8);

  &.with-side-margin {
    margin-left: var(--spacing-24);
    margin-right: var(--spacing-24);
  }
}
</style>
